import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Select - Radix UI Select wrapper with smart positioning
 *
 * Provides dropdown select with automatic collision detection and repositioning.
 * Use for form fields requiring single-choice selection (domain/project selectors, filters, etc.)
 *
 * Features:
 * - Dynamic positioning based on viewport space (opens above/below automatically)
 * - Portal rendering to avoid overflow clipping
 * - Theme-aware styling using design tokens
 * - Keyboard navigation (↑↓ arrows, Enter, Esc)
 * - Accessibility (ARIA, focus management)
 *
 * Usage:
 * ```tsx
 * <Select value={selectedId} onValueChange={setSelectedId} placeholder="Select a domain">
 *   {domains.map(d => (
 *     <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
 *   ))}
 * </Select>
 * ```
 */
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import * as RadixSelect from '@radix-ui/react-select';
import { IconSize } from '../tokens.js';
export function Select({ value, onValueChange, placeholder, disabled, required, loading, id, name, size = 'md', children, }) {
    const triggerClass = size === 'sm'
        ? 'select-trigger select-trigger--sm'
        : size === 'comfortable'
            ? 'select-trigger select-trigger--comfortable'
            : 'select-trigger';
    const contentClass = size === 'sm' ? 'select-content select-content--sm' : 'select-content';
    const iconSize = size === 'sm' ? IconSize.sm : IconSize.md;
    return (_jsxs(RadixSelect.Root, { value: value, onValueChange: onValueChange, disabled: disabled || loading, required: required, name: name, children: [_jsxs(RadixSelect.Trigger, { id: id, className: triggerClass, children: [_jsx(RadixSelect.Value, { placeholder: loading ? 'Loading...' : placeholder }), _jsx(RadixSelect.Icon, { className: "select-icon", children: loading ? (_jsx("span", { className: "select-spinner", "aria-hidden": "true" })) : (_jsx(ChevronDownIcon, { width: iconSize, height: iconSize })) })] }), _jsx(RadixSelect.Portal, { children: _jsx(RadixSelect.Content, { className: contentClass, side: "bottom", align: "start", sideOffset: 4, collisionPadding: 8, position: "popper", children: _jsx(RadixSelect.Viewport, { className: "select-viewport", children: children }) }) })] }));
}
export function SelectItem({ value, children, disabled }) {
    return (_jsxs(RadixSelect.Item, { className: "select-item", value: value, disabled: disabled, children: [_jsx(RadixSelect.ItemText, { className: "select-item__text", children: children }), _jsx(RadixSelect.ItemIndicator, { className: "select-item-indicator", children: _jsx(CheckIcon, { width: IconSize.md, height: IconSize.md, "aria-hidden": "true" }) })] }));
}
export const SelectSeparator = RadixSelect.Separator;
export const SelectGroup = RadixSelect.Group;
export const SelectLabel = RadixSelect.Label;
//# sourceMappingURL=Select.js.map